import { useInfiniteQuery } from '@tanstack/react-query';

import { expectStatus } from '@/lib/api/client';
import {
  getReadOrganizationAccessLogQueryKey,
  readOrganizationAccessLog,
  useListOrganizationWorkspaces,
} from '@/lib/api/generated/api';
import type { AccessLogPage } from '@/lib/api/generated/model';

/**
 * Every workspace, for the console (spec 191). Read whole and sliced here: the
 * filter's counts must be true for all of them, as the people list's are.
 */
export function useAdminWorkspaces() {
  const query = useListOrganizationWorkspaces();
  const response = query.data;
  return {
    data: response?.status === 200 ? response.data : undefined,
    isPending: query.isPending,
    isError:
      query.isError || (response !== undefined && response.status !== 200),
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  };
}

/**
 * Every workspace's access log for a range of days, newest first, by pages —
 * or one workspace's, when `workspaceId` names it (spec 195 US4).
 */
export function useOrganizationAccessLog(
  from: string,
  to: string,
  workspaceId?: string,
) {
  const enabled = from !== '' && to !== '' && from <= to;
  const params = {
    from,
    to,
    ...(workspaceId ? { workspace_id: workspaceId } : {}),
  };
  const query = useInfiniteQuery({
    queryKey: getReadOrganizationAccessLogQueryKey(params),
    queryFn: async ({ pageParam, signal }): Promise<AccessLogPage> =>
      expectStatus(
        await readOrganizationAccessLog(
          { ...params, ...(pageParam ? { before: pageParam } : {}) },
          { signal },
        ),
        200,
        'The activity could not be read.',
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before ?? null,
    enabled,
  });
  return {
    entries: query.data?.pages.flatMap((page) => page.items) ?? [],
    enabled,
    isPending: enabled && query.isPending,
    isError: query.isError,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    retry: () => void query.refetch(),
  };
}
